<div class="login-container">
  <!-- Full-page loading overlay during sign-in -->
  <div *ngIf="loading" class="login-loading-overlay">
    <div class="loading-spinner-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">{{ 'AUTH.LOGIN.SIGNING_IN_BUTTON' | translate }}</p>
    </div>
  </div>

  <!-- Centered Logo -->
  <div class="logo-header">
    <div class="brand-logo" (click)="goToHome()">
      <img src="logo.png" alt="Tenjah School Logo" class="logo-image">
    </div>
  </div>

  <!-- Language Selector -->
  <div class="language-selector" (click)="toggleLanguageDropdown()" [class.open]="isLanguageDropdownOpen">
    <div class="language-button">
      <div class="language-flag">{{getLanguageFlag(currentLanguage.code)}}</div>
      <span class="language-code">{{currentLanguage.code.toUpperCase()}}</span>
      <mat-icon class="dropdown-icon">arrow_drop_down</mat-icon>
    </div>
    <div class="language-dropdown" *ngIf="isLanguageDropdownOpen">
      <div class="language-option"
           *ngFor="let language of availableLanguages"
           [class.active]="language.code === currentLanguage.code"
           (click)="selectLanguage(language.code, $event)">
        <div class="language-flag">{{getLanguageFlag(language.code)}}</div>
        <span class="language-name">{{language.nativeName}}</span>
        <mat-icon class="check-icon" *ngIf="language.code === currentLanguage.code">check</mat-icon>
      </div>
    </div>
  </div>

  <!-- Left Panel - School Themed -->
  <div class="left-panel">
    <div class="school-illustration">
      <!-- Animated Background Elements -->
      <div class="illustration-background">
        <div class="floating-shape shape-1"></div>
        <div class="floating-shape shape-2"></div>
        <div class="floating-shape shape-3"></div>
        <div class="floating-shape shape-4"></div>
        <div class="gradient-orbs">
          <div class="orb orb-1"></div>
          <div class="orb orb-2"></div>
        </div>
      </div>

      <!-- Main School Illustration -->
      <div class="illustration-content">
        <div class="school-building">
          <div class="building-base"></div>
          <div class="building-roof"></div>
          <div class="building-flag"></div>
          <div class="building-windows">
            <div class="window window-1"></div>
            <div class="window window-2"></div>
            <div class="window window-3"></div>
            <div class="window window-4"></div>
            <div class="window window-5"></div>
            <div class="window window-6"></div>
          </div>
          <div class="building-door"></div>
        </div>

        <div class="school-elements">
          <div class="floating-book book-1">📚</div>
          <div class="floating-book book-2">📖</div>
          <div class="floating-book book-3">📝</div>
          <div class="graduation-cap">🎓</div>
          <div class="apple">🍎</div>
          <div class="microscope">🔬</div>
          <div class="globe">🌍</div>
          <div class="calculator">🧮</div>
        </div>

        <div class="welcome-text">
          <h2>{{ 'AUTH.LOGIN.WELCOME_TO' | translate }}</h2>
          <h1>{{ 'AUTH.LOGIN.PAGE_TITLE' | translate }}</h1>
          <p>{{ 'AUTH.LOGIN.TAGLINE' | translate }}</p>
          <div class="stats">
            <div class="stat">
              <span class="stat-number">{{ studentsCount }}+</span>
              <span class="stat-label">{{ 'AUTH.LOGIN.STATS.STUDENTS' | translate }}</span>
            </div>
            <div class="stat">
              <span class="stat-number">{{ teachersCount }}+</span>
              <span class="stat-label">{{ 'AUTH.LOGIN.STATS.TEACHERS' | translate }}</span>
            </div>
            <div class="stat">
              <span class="stat-number">{{ coursesCount }}+</span>
              <span class="stat-label">{{ 'AUTH.LOGIN.STATS.COURSES' | translate }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Right Panel - Login Form -->
  <div class="right-panel">

    <!-- Animated Background for Right Panel -->
    <div class="animated-background">
      <div class="floating-shape shape-5"></div>
      <div class="floating-shape shape-6"></div>
      <div class="gradient-orbs">
        <div class="orb orb-3"></div>
      </div>
    </div>

    <!-- Login Form Wrapper -->
    <div class="login-wrapper">
      <mat-card class="login-card">
      <!-- Header with Animated Logo -->
      <mat-card-header class="animated-header">
        <div class="logo-container">
          <div class="logo-wrapper">
            <div class="logo-icon">
              <mat-icon>school</mat-icon>
              <div class="icon-glow"></div>
            </div>
            <div class="logo-particles">
              <span class="particle particle-1">✨</span>
              <span class="particle particle-2">📚</span>
              <span class="particle particle-3">🎓</span>
              <span class="particle particle-4">💡</span>
            </div>
          </div>
          <div class="header-content">
            <mat-card-title class="animated-title">
              <span class="title-text">{{ 'AUTH.LOGIN.CARD_TITLE' | translate }}</span>
              <div class="title-underline"></div>
            </mat-card-title>
            <mat-card-subtitle class="animated-subtitle">
              {{ 'AUTH.LOGIN.CARD_SUBTITLE' | translate }}
            </mat-card-subtitle>
          </div>
        </div>
      </mat-card-header>

      <!-- Form Content -->
      <mat-card-content class="form-content">
        <form [formGroup]="form" (ngSubmit)="submit()" class="login-form">
          <!-- Email Field -->
          <div class="input-group">
            <mat-form-field appearance="outline" class="modern-input">
              <mat-label>{{ 'AUTH.LOGIN.EMAIL_LABEL' | translate }}</mat-label>
              <input matInput type="email" formControlName="email" [placeholder]="'AUTH.LOGIN.EMAIL_PLACEHOLDER' | translate">
              <mat-icon matSuffix class="input-icon">email</mat-icon>
              <mat-error *ngIf="form.controls['email'].hasError('required')">
                {{ 'AUTH.LOGIN.EMAIL_REQUIRED' | translate }}
              </mat-error>
              <mat-error *ngIf="form.controls['email'].hasError('email')">
                {{ 'AUTH.LOGIN.EMAIL_INVALID' | translate }}
              </mat-error>
            </mat-form-field>
            <div class="field-animation"></div>
          </div>

          <!-- Password Field -->
          <div class="input-group">
            <mat-form-field appearance="outline" class="modern-input">
              <mat-label>{{ 'AUTH.LOGIN.PASSWORD_LABEL' | translate }}</mat-label>
              <input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="password">
              <button mat-icon-button matSuffix (click)="hidePassword = !hidePassword" type="button" class="visibility-toggle">
                <mat-icon>{{hidePassword ? 'visibility_off' : 'visibility'}}</mat-icon>
              </button>
              <mat-error *ngIf="form.controls['password'].hasError('required')">
                {{ 'AUTH.LOGIN.PASSWORD_REQUIRED' | translate }}
              </mat-error>
            </mat-form-field>
            <div class="field-animation"></div>
          </div>

          <!-- Error Message -->
          <div class="error-message" *ngIf="error">
            <div class="error-icon">
              <mat-icon>error</mat-icon>
            </div>
            <span class="error-text">{{ error }}</span>
            <div class="error-animation"></div>
          </div>

          <!-- Login Button -->
          <button mat-raised-button color="primary" type="submit" [disabled]="loading" class="modern-login-button">
            <div class="button-content">
              <mat-icon *ngIf="loading" class="loading-icon">refresh</mat-icon>
              <span class="button-text" *ngIf="!loading">{{ 'AUTH.LOGIN.SIGN_IN_BUTTON' | translate }}</span>
              <span class="button-text" *ngIf="loading">{{ 'AUTH.LOGIN.SIGNING_IN_BUTTON' | translate }}</span>
              <div class="button-ripple"></div>
            </div>
            <div class="button-glow"></div>
          </button>
        </form>
      </mat-card-content>

      <!-- Dev Accounts Footer -->
      <mat-card-footer class="dev-section" *ngIf="!environment.production">
        <mat-expansion-panel class="dev-accounts">
          <mat-expansion-panel-header>
            <mat-panel-title class="dev-panel-title">
              <mat-icon class="dev-icon">code</mat-icon>
              <span>{{ 'AUTH.LOGIN.DEV_SECTION.TITLE' | translate }}</span>
              <div class="panel-glow"></div>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div class="account-grid">
            <div class="account-card" (click)="fillCredentials('admin@school.tn', 'Admin123!')">
              <div class="account-icon admin-icon">
                <mat-icon>admin_panel_settings</mat-icon>
              </div>
              <span class="account-label">{{ 'AUTH.LOGIN.DEV_SECTION.ADMIN' | translate }}</span>
              <div class="card-hover-effect"></div>
            </div>
            <div class="account-card" (click)="fillCredentials('teacher1@school.tn', 'Teacher123!')">
              <div class="account-icon teacher-icon">
                <mat-icon>person</mat-icon>
              </div>
              <span class="account-label">{{ 'AUTH.LOGIN.DEV_SECTION.TEACHER' | translate }}</span>
              <div class="card-hover-effect"></div>
            </div>
            <div class="account-card" (click)="fillCredentials('iheb@hk', '123456789')">
              <div class="account-icon student-icon">
                <mat-icon>school</mat-icon>
              </div>
              <span class="account-label">{{ 'AUTH.LOGIN.DEV_SECTION.STUDENT' | translate }}</span>
              <div class="card-hover-effect"></div>
            </div>
            <div class="account-card" (click)="fillCredentials('parent1@school.tn', 'Parent123!')">
              <div class="account-icon parent-icon">
                <mat-icon>family_restroom</mat-icon>
              </div>
              <span class="account-label">{{ 'AUTH.LOGIN.DEV_SECTION.PARENT' | translate }}</span>
              <div class="card-hover-effect"></div>
            </div>
          </div>
        </mat-expansion-panel>
      </mat-card-footer>
      </mat-card>
    </div>
  </div>
</div>
